import { Badge } from "@/components/ui/badge";
import type {
  ApprovalDecisionPayload,
  NormalizedEvent,
} from "@/services/types/agent-gateway";
import { ShieldCheck } from "lucide-react";
import { AgentError } from "./AgentError";
import { AgentMessage } from "./AgentMessage";
import { AgentStatus } from "./AgentStatus";
import { ApprovalCard, RecordedApprovalCard } from "./ApprovalCard";
import { ArtifactCard } from "./ArtifactCard";
import { RequestInputCard } from "./RequestInputCard";
import { ToolActivity } from "./ToolActivity";
import { formatEventAuthor, formatEventStatus } from "./event-utils";

type AgentEventProps = {
  event: NormalizedEvent;
  sessionActive?: boolean;
  canApproveVerifiedLeads?: boolean;
  canApproveOutboundActions?: boolean;
  pendingApprovalId?: string | null;
  approvalError?: string | null;
  onApprovalDecision?: (payload: ApprovalDecisionPayload) => void;
  pendingContinuationId?: string | null;
  onRetryApprovalContinuation?: (approvalId: string) => void;
  answeredInputResponse?: unknown;
  inputAnswered?: boolean;
  pendingInputId?: string | null;
  inputError?: string | null;
  onRequestInputResponse?: (interruptId: string, response: unknown) => void;
};

export function AgentEvent({
  event,
  sessionActive = true,
  canApproveVerifiedLeads = false,
  canApproveOutboundActions = false,
  pendingApprovalId,
  approvalError,
  onApprovalDecision,
  pendingContinuationId,
  onRetryApprovalContinuation,
  answeredInputResponse,
  inputAnswered,
  pendingInputId,
  inputError,
  onRequestInputResponse,
}: AgentEventProps) {
  switch (event.type) {
    case "message":
      return <AgentMessage event={event} />;

    case "agent_status":
      return <AgentStatus event={event} />;

    case "tool_started":
    case "tool_completed":
      return <ToolActivity event={event} />;

    case "artifact":
      return <ArtifactCard event={event} />;

    case "error":
      return <AgentError event={event} />;

    case "approval_required":
      if ("approval_type" in event.content) {
        const canApprove =
          event.content.approval_type === "verified_leads"
            ? canApproveVerifiedLeads
            : canApproveOutboundActions;
        return (
          <ApprovalCard
            actionsDisabled={pendingApprovalId !== null}
            agentName={formatEventAuthor(event.author)}
            canApprove={sessionActive && canApprove}
            errorMessage={approvalError}
            isPending={pendingApprovalId === event.content.approval_id}
            onDecision={onApprovalDecision ?? (() => undefined)}
            proposal={event.content}
          />
        );
      }
      return (
        <div className="flex min-w-0 flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <ShieldCheck
            aria-hidden="true"
            className="size-5 shrink-0 text-amber-700"
          />
          <span className="min-w-0 flex-1 text-amber-900 text-sm [overflow-wrap:anywhere]">
            {event.content.label ?? "Approval required"}
          </span>
          <Badge className="rounded-full" variant="outline">
            Read only
          </Badge>
        </div>
      );

    case "approval_recorded":
      if ("approval_id" in event.content && "decided_by" in event.content) {
        return (
          <RecordedApprovalCard
            agentName={formatEventAuthor(event.author)}
            isRetryPending={pendingContinuationId === event.content.approval_id}
            onRetryContinuation={
              sessionActive ? onRetryApprovalContinuation : undefined
            }
            record={event.content}
          />
        );
      }
      return (
        <div className="flex min-w-0 flex-wrap items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
          <ShieldCheck
            aria-hidden="true"
            className="size-5 shrink-0 text-green-700"
          />
          <span className="min-w-0 flex-1 text-green-900 text-sm [overflow-wrap:anywhere]">
            {event.content.label}
          </span>
          <Badge className="rounded-full" variant="outline">
            {formatEventStatus(event.content.status)}
          </Badge>
        </div>
      );

    case "input_required":
      return (
        <RequestInputCard
          answered={inputAnswered}
          answeredResponse={answeredInputResponse}
          disabled={!sessionActive}
          errorMessage={inputError ?? undefined}
          isPending={pendingInputId === event.content.interrupt_id}
          onSubmit={(response) =>
            onRequestInputResponse?.(event.content.interrupt_id, response)
          }
          request={event.content}
        />
      );

    case "input_recorded":
      return null;
  }
}
